import { useState } from "react";
import * as api from "../lib/api";
import type { Project } from "../lib/api";

// Edit an app's discovery details — name (required), elevator-pitch blurb, and tags.
// Opened from the Studio topbar, and auto-opened when a deploy is rejected for a missing
// name. The agent also sets these via set_project_metadata; this is the manual editor.
export function AppDetailsModal({
  project,
  hint,
  onSaved,
  onClose,
}: {
  project: Project;
  /** Optional nudge shown at the top, e.g. when opened because deploy needs a name. */
  hint?: string | null;
  onSaved: (next: { name: string; description: string; tags: string[] }) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(isDefaultName(project.name) ? "" : project.name);
  const [description, setDescription] = useState(project.description ?? "");
  const [tagsText, setTagsText] = useState(project.tags.join(", "));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsedTags = tagsText
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean)
    .slice(0, 8);

  const nameOk = name.trim().length > 0 && !isDefaultName(name.trim());

  async function onSave() {
    if (!nameOk) {
      setError("Please enter a name.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.renameProject(project.id, name.trim());
      await api.setProjectMetadata(project.id, { description: description.trim(), tags: parsedTags });
      onSaved({ name: name.trim(), description: description.trim(), tags: parsedTags });
    } catch (e) {
      setError((e as Error).message ?? "failed to save");
      setSaving(false);
    }
  }

  return (
    <div className="modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ width: 460, maxWidth: "96vw" }}>
        <h2>App details</h2>
        {hint && (
          <div className="hint" style={{ marginTop: 0, marginBottom: 12, color: "var(--accent)" }}>{hint}</div>
        )}
        <div className="row">
          <div className="label">Name <span style={{ color: "var(--fg-faint)" }}>(required)</span></div>
          <input
            autoFocus
            value={name}
            placeholder="e.g. Asteroid Blaster"
            maxLength={60}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="row">
          <div className="label">Elevator pitch</div>
          <textarea
            value={description}
            placeholder="One sentence a player reads to decide whether to try it — what it is + the vibe."
            maxLength={500}
            rows={3}
            style={{ width: "100%", resize: "vertical", background: "var(--panel)", color: "var(--fg)", border: "1px solid var(--border-2)", borderRadius: 6, padding: "8px 10px", fontSize: 13, fontFamily: "inherit" }}
            onChange={(e) => setDescription(e.target.value)}
          />
          <div className="hint">{description.length}/500 — shown on the app's hub card and searched.</div>
        </div>
        <div className="row">
          <div className="label">Tags</div>
          <input
            value={tagsText}
            placeholder="trivia, party, team (comma-separated, max 8)"
            onChange={(e) => setTagsText(e.target.value)}
          />
          {parsedTags.length > 0 && (
            <div className="hint">
              {parsedTags.map((t) => <span key={t} className="detail-tag">{t}</span>)}
            </div>
          )}
        </div>
        {error && <div className="mono status-err" style={{ fontSize: 12 }}>{error}</div>}
        <div className="actions" style={{ justifyContent: "flex-end" }}>
          <button onClick={onClose} disabled={saving}>Cancel</button>
          <button className="primary" onClick={onSave} disabled={saving || !nameOk}>
            {saving ? <><span className="spinner" /> Saving…</> : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function isDefaultName(n: string): boolean {
  return /^untitled\b/i.test(n.trim());
}
